// fetch statt axios — gleiche Schnittstelle, 52 KB weniger (httpClient.js).
import http from './httpClient.js';

/* Das Backend meldet eine abgelaufene Sitzung mit genau diesem Text (ohne
   Punkt). _post verglich mit 'Session expired.' und erkannte sie deshalb nie;
   der Check-in pollt per POST und zeigte nach Ablauf „Nicht berechtigt" statt
   der Anmeldeseite (Security-Audit 09.09.2026, #126). */
const SESSION_EXPIRED = 'Session expired';

/* Nur Endpunkt, Code, Status und Meldung loggen: ein Fehlerobjekt kann den
   Request-Body tragen (bei axios war es config.data), beim Login also das
   Passwort, und die Konsole läuft auch auf dem Tablet am Eingang (#126). */
function logApiError(endpoint, error) {
    console.error('API request failed:', endpoint, error?.code, error?.response?.status, error?.message);
}

export default class ApiHandler {
    constructor() {
        this.api_url = (process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:3030");

        this.client = http.create({ timeout: 15000 });
        this.loggedIn = false;
        this.group = {};
        // Wird von App gesetzt: läuft die Sitzung während der Arbeit ab, kann
        // React die Anmeldeseite zeigen, statt bis zum Reload zu warten (#126).
        this.onSessionExpired = null;
    }

    async _post(url, query = {}) {
        let res = {};
        try {
            res = await this.client.post(
                `${this.api_url}/api/v1/${url}`,
                query,
                {
                    withCredentials: true
                }
            );

            res = res.data;
        } catch (error) {
            logApiError(url, error);

            // Timeout (no response in time)
            if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''))
                return { success: false, data: "Der Server antwortet gerade nicht. Bitte versuch es noch einmal." };

            // Server responded with an error status
            if (error.response) {
                const status = error.response.status;
                const backendMsg = error.response.data && error.response.data.data;
                if (status === 401 || status === 403)
                    return { success: false, data: backendMsg || "Anmeldung fehlgeschlagen – Benutzername oder Passwort falsch." };
                if (status >= 500)
                    return { success: false, data: "Auf dem Server ist etwas schiefgelaufen. Bitte versuch es später noch einmal." };
                return { success: false, data: backendMsg || `Unerwarteter Fehler (Status ${status}).` };
            }

            // Request sent but no response received → network / server down
            if (error.request)
                return { success: false, data: "Keine Verbindung zum Server. Bitte prüf deine Internetverbindung." };

            return { success: false, data: "Etwas ist schiefgelaufen. Bitte versuch es noch einmal." };
        }

        if (res.success === false && res.data === SESSION_EXPIRED)
            this.setLoggedIn(false);

        if(res.success === true)
            this.setLoggedIn(true);

        return res;
    }

    async _request(url, query = {}) {
        let ret = await this.client.get(
            `${this.api_url}/api/v1/${url}`,
            {
                params: query,
                withCredentials: true
            }
        );

        let body = ret;

        if ("body" in body)
            body = body.body;

        if ("data" in body)
            body = body.data;

        if (body.success === false && body.data === SESSION_EXPIRED)
            this.setLoggedIn(false);

        if(body.success === true)
            this.setLoggedIn(true);

        return body;
    }

    setLoggedIn(log) {
        const was = this.loggedIn;
        this.loggedIn = log;
        if (was && !log && typeof this.onSessionExpired === 'function') this.onSessionExpired();
    }

    async getLoggedIn()
    {
        this.loggedIn =(await this._request("getSession")).success;

        return this.loggedIn;
    }

    async login(username, password) {
        this._profil = null;
        let res = await this._post("auth", { username: username, password: password });

        if (res.success)
            this.group = res.data;

        return res;
    }

    async logout() {
        // POST wie im Backend: Abmelden ist eine Zustandsänderung (#110).
        let res = await this._post("logout");

        this.user = null;
        this.group = {};
        this.loggedIn = false;
        this._profil = null;

        return res;
    }

    /* Feedback-Knopf → GitHub-Issue (backend feedbackIssue.js). false, wenn
       die Funktion aus ist oder der Server nicht antwortet — dann bleibt der
       Knopf bei der Mail. */
    async getFeedbackEnabled() {
        try {
            const res = await this._request("feedback");
            return !!(res.success && res.data?.enabled);
        } catch {
            return false;
        }
    }

    /* { success: true, data: { number } } oder { success: false, data: Meldung } */
    async sendFeedback(feedback) {
        return await this._post("feedback", feedback);
    }

    async getGroup() {
        let res = await this._request("group");

        if (!res.success)
            return false;

        this.group = res.data;
        return res.data;
    }

    async getEvents(organizer) {
        let res = await this._request("events/" + organizer);

        if (!res.success)
            return [];

        return res.data;
    }

    async getTickets(organizer, event, namiId = null, page = 0, limit = null) {
        const params = new URLSearchParams({ page: String(page) });
        if (limit !== null && limit !== undefined) params.set('limit', String(limit));
        let res = await this._request("events/" + organizer + "/" + event + "?" + params.toString());

        if (!res.success)
            return [];

        return res.data;
    }

    async getGroups(organizer, event) {
        let res = await this._request("events/" + organizer + "/" + event + "/groups");

        if (!res.success)
            return [];
        return res.data;
    }

    async getGroupTickets(organizer, event, groupId, page = 0, limit = 50, stageFilter = null, statusFilter = null, fields = null, search = null, advFilters = null) {
        const gid = (groupId === "all" || !groupId) ? "null" : groupId;
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (stageFilter && stageFilter.length > 0) params.set('stage', stageFilter.join(','));
        if (statusFilter && statusFilter.length > 0) params.set('status', statusFilter.join(','));
        if (fields) params.set('fields', fields.join(','));
        if (search) params.set('search', search);
        if (advFilters && advFilters.length > 0) params.set('advFilters', JSON.stringify(advFilters));
        let res = await this._request("events/" + organizer + "/" + event + "/groups/" + gid + "?" + params.toString());

        if (!res.success)
            return { data: [], hasMore: false };

        return { data: res.data, hasMore: res.hasMore || false, totalCount: res.totalCount || 0, groupTotalCount: res.groupTotalCount || 0, paidCount: res.paidCount || 0, notPaidCount: res.notPaidCount || 0, missing2deCount: res.missing2deCount || 0, stageCounts: res.stageCounts || {} };
    }

    async getSubsidyList(organizer, event) {
        let res = await this._post("getSubsidyList", { organizer: organizer, event: event });
        return res.success ? res.data : null;
      }

    async getDailyAttendance(organizer, event) {
        let res = await this._post("getDailyAttendance", { organizer: organizer, event: event });

        return res.success ? res.data : null;
     }

     async setSubsidyConfig(organizer, event, subsidyConfig) {
        let res = await this._post("setSubsidyConfig", { organizer: organizer, event: event, subsidyConfig: subsidyConfig });

        return res;
     }

    async getMyTickets(organizer, event, page = 0, limit = null) {
        const params = new URLSearchParams({ page: String(page) });
        if (limit !== null && limit !== undefined) params.set('limit', String(limit));
        let res = await this._request("my-tickets/" + organizer + "/" + event + "?" + params.toString());

        if (!res.success)
            return { data: [], totalCount: 0 };

        return res.data;
    }

    async getTicketCounts(organizer, event) {
        let res = await this._request("events/" + organizer + "/" + event + "/ticket-counts");

        if (!res.success || !res.data)
            return { total: 0, myCount: 0 };

        return res.data;
    }

    async getQuestions(organizer, event) {
        let res = await this._request("events/" + organizer + "/" + event + "/questions");

        if (!res.success)
            return [];

        return res.data;
    }

    async setQuestionShow(id, show, organizer, event) {
        let res = await this._post("question", {id: id, show: show, organizer: organizer, event: event});

        return res;
    }

    async set2de(id, date) {
        let res = await this._post("set2de", {namiId: id, last2de: date});

        return res;
    }

    async setCoc(id, date) {
        let res = await this._post("setCoc", {namiId: id, lastCoc: date});

        return res;
    }

    async setTicketOverride(organizer, event, code, positionId, field, value) {
        let res = await this._post("setTicketOverride", { organizer, event, code, positionId, field, value });
        return res;
    }

    async getTicketOverride(organizer, event, code, positionId) {
        const params = new URLSearchParams({ organizer, event, code, positionId });
        let res = await this._request("getTicketOverride?" + params.toString());
        if (!res.success) return null;
        return res.data;
    }

    async canEditNamiUser(id) {
        let res = await this._request("canEditNamiUser/" + id);

        if (!res.success)
            return false;

        return res.data;
    }

    async changeNamiID(organizer, event, code, positionId, namiId) {
        let res = await this._post("changeNamiId", {
            organizer: organizer, event: event, code: code, positionId: positionId, namiId: namiId
        });

        return res;
    }

    async setVisibleColumns(organizer, event, columns){
        let res = await this._post("setVisibleColumns", {organizer: organizer, event: event, columns: columns});

        return res;
    }

    async changeLeader(organizer, event, stageConfig) {
        let res = await this._post("setStageFilter", {organizer: organizer, event: event, stageConfig: stageConfig});
        return res;
    }

    async setAttendanceDays(organizer, event, code, positionId, days) {
        let res = await this._post("setAttendanceDays", {
            organizer: organizer, event: event, code: code, positionId: positionId,
            days: days.map(d => (d instanceof Date ? d.toISOString() : d))
        });

        return res;
     }

    // #check-in-store U6/U7 — operator check-in store.
    async getCheckIn(organizer, event) {
        let res = await this._post("getCheckIn", { organizer: organizer, event: event });

        return res.success ? res.data : null;
     }

    async setCheckInDefault(organizer, event, which) {
        let res = await this._post("setCheckInDefault", {
            organizer: organizer, event: event, which: which
         });

        return res;
     }

    async markProcessed(organizer, event, code, positionId, processed = true) {
        let res = await this._post("markProcessed", {
            organizer: organizer, event: event, code: code, positionId: positionId, processed: processed
         });

        return res;
     }

    async setLeaveFlags(organizer, event, code, positionId, bildungs, sonder) {
        let res = await this._post("setLeaveFlags", {
            organizer: organizer, event: event, code: code, positionId: positionId,
            bildungs: bildungs, sonder: sonder
         });

        return res;
     }

    async setEventConfig(organizer, event, config) {
        let res = await this._post("setEventConfig", {
            organizer: organizer, event: event,
            ...config
        });

        return res;
    }

    // Mit organizer/event prüft der Aufruf den whole-event-Status der gewählten
    // Veranstaltung; ohne Argumente bleibt der globale Super-Admin-Check.
    async isAdmin(organizer, event) {
        const q = new URLSearchParams();
        if (organizer) q.set("organizer", organizer);
        if (event) q.set("event", event);
        const qs = q.toString();
        let res = await this._request("isAdmin" + (qs ? "?" + qs : ""));

        if (!res.success)
            return false;

        return res.data;
     }

    async setDashboardAccess(organizer, event, groupId, namiId, val) {
        let res = await this._post("setDashboardAccess", {organizer: organizer, event: event, groupId: groupId, namiId: namiId, value: val});

        return res;
    }

    async getDashboardAccess(organizer, event, groupId, namiId) {
        let res = await this._request("getDashboardAccess/" + organizer + "/" + event + "/" + groupId + "/" + namiId);

        if (!res.success)
            return false;

        return res.data;
    }

    async getGrantedRights(organizer, event, namiId) {
        let res = await this._request("getGrantedRights/" + organizer + "/" + event + "/" + namiId);

        if (!res.success || !Array.isArray(res.data))
            return [];

        return res.data.map(g => g.toString());
    }

    async getNamiAdminGroups() {
        let res = await this._request("getNamiAdminGroups");

        if (!res.success || !Array.isArray(res.data))
            return [];

        return res.data.map(g => g.toString());
    }

    async checkRights(organizer, event, namiId) {
        let res = await this._request("checkRights/" + organizer + "/" + event + "/" + namiId);

        if (!res.success || !res.data)
            return { adminGroups: [], grantedGroups: [], disableAutoAdmin: true };

        return res.data;
    }

    async getCallerNamiID() {
        let res = await this._request("getCallerNamiID");

        if (!res.success)
            return null;

        return res.data;
    }

    /* Startseite (Gruß) und Seitenleiste (Profil unten) fragen beide; einmal
       pro Anmeldung reicht. login() und logout() vergessen es wieder. Ein
       Fehlschlag wird nicht gemerkt. */
    getCallerProfile() {
        if (!this._profil) {
            this._profil = this._request("getCallerProfile")
                .then((res) => (res.success ? res.data : null))
                .catch(() => null);
            this._profil.then((p) => {
                if (p === null) this._profil = null;
            });
        }
        return this._profil;
    }

    async getGrantedEvents() {
        let res = await this._request("getGrantedEvents");

        if (!res.success)
            return [];

        return res.data;
    }

    async getNamiUsers(page = 0, limit = 50, filters = {}) {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (filters.search) params.set('search', filters.search);
        if (filters.roles && filters.roles.length > 0) params.set('roles', filters.roles.join(','));
        if (filters.group) params.set('group', filters.group);

        let res = await this._request(`nami-users?${params.toString()}`);

        if (!res.success || !Array.isArray(res.data))
            return { data: [], total: 0, hasMore: false };

        return { data: res.data, total: res.total, hasMore: res.hasMore };
    }

    async getNamiUserCounts(filters = {}) {
        const params = new URLSearchParams();
        if (filters.search) params.set('search', filters.search);
        if (filters.roleFilter && filters.roleFilter.length) params.set('roles', filters.roleFilter.join(','));
        if (filters.groupFilter) params.set('group', filters.groupFilter);
        const qs = params.toString();
        let res = await this._request("nami-users/counts" + (qs ? '?' + qs : ''));

        if (!res.success)
            return { total: 0, roles: {}, groups: {} };

        return { total: res.total, roles: res.roles, groups: res.groups };
    }

    // #29 Part B3 — Teilnehmerzertifikate

    async setCertificateConfig(organizer, event, certificateConfig) {
        let res = await this._post("setCertificateConfig", {
            organizer: organizer, event: event, certificateConfig: certificateConfig
        });

        return res;
    }

    async uploadCertificateTemplate(organizer, event, payload) {
        // docxBase64 up to ~7 MB base64 → longer timeout than the 15 s default.
        let res = {};
        try {
            res = await this.client.post(
                `${this.api_url}/api/v1/uploadCertificateTemplate`,
                { organizer: organizer, event: event, ...payload },
                { withCredentials: true, timeout: 60000 }
            );
            res = res.data;
        } catch (error) {
            logApiError('uploadCertificateTemplate', error);
            if (error.code === 'ECONNABORTED')
                return { success: false, data: "Der Server antwortet gerade nicht. Bitte versuch es noch einmal." };
            return { success: false, data: "Das Upload ist fehlgeschlagen. Bitte versuch es noch einmal." };
        }

        // Auch bei eigenem Timeout gilt: meldet der Backend die Sitzung ab,
        // muss die App zur Anmeldeseite, nicht bis zum Reload warten (#126).
        if (res.success === false && res.data === SESSION_EXPIRED) this.setLoggedIn(false);
        if (res.success === true) this.setLoggedIn(true);
        return res;
    }

    async deleteCertificateTemplate(organizer, event, templateId) {
        let res = await this._post("deleteCertificateTemplate", {
            organizer: organizer, event: event, templateId: templateId
        });

        return res;
    }

    async sendCertificates(organizer, event, templateId, dryRun) {
        let res = {};
        try {
            res = await this.client.post(
                `${this.api_url}/api/v1/sendCertificates`,
                { organizer: organizer, event: event, templateId: templateId, dryRun: dryRun === true },
                { withCredentials: true, timeout: 180000 }
            );
            res = res.data;
        } catch (error) {
            logApiError('sendCertificates', error);
            if (error.code === 'ECONNABORTED')
                return { success: false, data: "Der Server antwortet gerade nicht (Versand dauert bei vielen Teilnehmern). Bitte prüf das Ergebnis auf dem Server." };
            return { success: false, data: "Der Versand ist fehlgeschlagen. Bitte versuch es noch einmal." };
        }

        // Dasselbe gilt beim Versand: die Sitzung kann während der 180 s
        // ablaufen; die App muss es sofort merken, nicht erst beim Reload.
        if (res.success === false && res.data === SESSION_EXPIRED) this.setLoggedIn(false);
        if (res.success === true) this.setLoggedIn(true);
        return res;
    }

    async getCertificateSendStatus(organizer, event, templateId) {
        let res = await this._post("getCertificateSendStatus", {
            organizer: organizer, event: event, templateId: templateId
        });

        return res.success ? res.data : null;
    }
}
